import React, { useEffect, useState } from 'react';
import {
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
  Row,
  Col,
  Label,
  Input,
  Button,
  Spinner,
  Badge,
} from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { bulkOrderData, bulkOrderStatus } from '../../actions/orderActions';
import toast, { Toaster } from 'react-hot-toast';
import AiBadgeIcon from './AiBadgeIcon';

const InfoField = ({ icon, label, value }) => (
  <div className="d-flex align-items-start gap-2">
    <i className={`bx ${icon} text-muted fs-5 mt-1`}></i>
    <div>
      <div className="text-muted text-uppercase fs-11 fw-semibold">{label}</div>
      <div className="fw-medium">{value || '-'}</div>
    </div>
  </div>
);

const BulkOrderEditOffcanvas = ({ isOpen, toggle, bulkId, onSaved }) => {
  const dispatch = useDispatch();
  const [remarks, setRemarks] = useState('');
  const [status, setStatus] = useState('');
  const [saving, setSaving] = useState(false);

  const { bulkOrder = [], isBulkOrderLoading } = useSelector(
    (state) => state.order
  );
  const { bulkStatus } = useSelector((state) => state.order);

  const record = bulkOrder?.OrderDetails?.[0]?.[0];
  const statusOptions = record?.status === '0' ? ['Pending', 'Completed'] : ['Completed'];
  const isPending = (status || record?.status) === '0';
  const isBulk = Number(record?.qty) > 3;
  const isYara = Number(record?.is_yara) === 1;

  useEffect(() => {
    if (!isOpen || !bulkId) return;
    bulkOrderData({ params: { bulk_id: bulkId }, dispatch });
  }, [isOpen, bulkId]);

  useEffect(() => {
    if (!isOpen) return;
    setRemarks(record?.remarks || '');
    setStatus(record?.status || '');
  }, [isOpen, record]);

  useEffect(() => {
    if (!saving || !bulkStatus) return;
    setSaving(false);
    if (bulkStatus?.code === 100 && bulkStatus?.status === 'success') {
      toast.success(bulkStatus?.message || 'Updated successfully', {
        style: { border: '1px solid #fff', padding: '20px', color: '#fff', background: '#4CAF50' },
        iconTheme: { primary: '#FFFAEE', secondary: '#4CAF50' },
      });
      onSaved?.();
      toggle();
    } else {
      toast.error(bulkStatus?.message || 'Failed to update.', {
        style: { border: '1px solid #fff', padding: '20px', color: '#fff', background: '#f44336' },
        iconTheme: { primary: '#FFFAEE', secondary: '#f44336' },
      });
    }
  }, [bulkStatus]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSaving(true);
    bulkOrderStatus({ params: { bulk_id: bulkId, remarks, status }, dispatch });
  };

  return (
    <Offcanvas
      isOpen={isOpen}
      toggle={toggle}
      direction="end"
      style={{ width: '440px', maxWidth: '92vw' }}
    >
      <OffcanvasHeader toggle={toggle} className="border-bottom bg-light">
        <div className="d-flex align-items-center flex-wrap gap-2">
          <span className="fw-bold">Bulk Order</span>
          {record?.id ? <span className="text-muted">#{record.id}</span> : null}
          {isPending ? (
            <Badge className="bg-soft-warning text-dark">Pending</Badge>
          ) : (
            <Badge className="bg-success">Completed</Badge>
          )}
          {isBulk ? <Badge className="bg-danger">Bulk order</Badge> : null}
          {isYara ? (
            <Badge className="bg-primary d-inline-flex align-items-center gap-1">
              <AiBadgeIcon size={20} color="#fff" title={null} />
              Yara AI
            </Badge>
          ) : null}
        </div>
      </OffcanvasHeader>
      <OffcanvasBody className="p-0">
        {isBulkOrderLoading ? (
          <div className="d-flex justify-content-center py-5">
            <Spinner color="primary" />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="d-flex flex-column" style={{ minHeight: '100%' }}>
            <div className="p-4 flex-grow-1" style={{ overflowY: 'auto' }}>
              <div className="border rounded-3 p-3 mb-3 bg-soft-light">
                <h6 className="text-muted text-uppercase fw-bold fs-12 mb-3">
                  <i className="bx bx-user-circle me-1"></i> Customer
                </h6>
                <Row className="g-3">
                  <Col xs={6}>
                    <InfoField icon="bx-user" label="Name" value={record?.full_name} />
                  </Col>
                  <Col xs={6}>
                    <InfoField icon="bx-phone" label="Mobile" value={record?.mobile_number} />
                  </Col>
                  <Col xs={6}>
                    <InfoField icon="bx-envelope" label="Email" value={record?.email} />
                  </Col>
                  <Col xs={6}>
                    <InfoField icon="bx-map" label="City" value={record?.city} />
                  </Col>
                </Row>
              </div>

              <div className="border rounded-3 p-3 mb-3 bg-soft-light">
                <h6 className="text-muted text-uppercase fw-bold fs-12 mb-3">
                  <i className="bx bx-package me-1"></i> Order
                </h6>
                <Row className="g-3">
                  <Col xs={6}>
                    <InfoField icon="bx-barcode" label="SKU" value={record?.sku} />
                  </Col>
                  <Col xs={6}>
                    <InfoField icon="bx-cube" label="Qty" value={record?.qty} />
                  </Col>
                </Row>
                {record?.comments ? (
                  <div className="mt-3 pt-3 border-top">
                    <div className="text-muted text-uppercase fs-11 fw-semibold mb-1">
                      Customer Comments
                    </div>
                    <div className="fst-italic">&ldquo;{record.comments}&rdquo;</div>
                  </div>
                ) : null}
              </div>

              <div>
                <h6 className="text-muted text-uppercase fw-bold fs-12 mb-3">
                  <i className="bx bx-edit-alt me-1"></i> Update
                </h6>
                <div className="mb-3">
                  <Label className="form-label">Remarks*</Label>
                  <Input
                    type="textarea"
                    rows={3}
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    placeholder="Add a remark..."
                    required
                  />
                </div>
                <div>
                  <Label className="form-label">Status*</Label>
                  <Input
                    type="select"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                  >
                    {statusOptions.map((item) => (
                      <option key={item} value={item === 'Pending' ? '0' : '1'}>
                        {item}
                      </option>
                    ))}
                  </Input>
                </div>
              </div>
            </div>

            <div className="d-flex justify-content-end gap-2 p-3 border-top bg-white">
              <Button outline color="secondary" type="button" onClick={toggle} disabled={saving}>
                Cancel
              </Button>
              <Button color="success" type="submit" disabled={saving}>
                {saving ? <Spinner size="sm" /> : 'Save Changes'}
              </Button>
            </div>
          </form>
        )}
      </OffcanvasBody>
      <Toaster position="top-right" reverseOrder={false} />
    </Offcanvas>
  );
};

export default BulkOrderEditOffcanvas;
